import { apiClient, apiClientEnvelope, ApiError } from '@/lib/api-client';
import type { SessionBillRow, ShiftReportFilters, ShiftReportResponse } from './types';

const emptyResponse: ShiftReportResponse = {
  data: [],
  meta: { page: 1, limit: 50, total: 0, totalPages: 0 },
  totals: { sessions: 0, bills: 0, sales_paise: 0, cash_paise: 0, hours: 0 },
};

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function toQuery(filters: ShiftReportFilters): string {
  const qs = new URLSearchParams();
  if (filters.start_date) qs.set('start_date', filters.start_date);
  if (filters.end_date) qs.set('end_date', filters.end_date);
  if (filters.location_id) qs.set('location_id', filters.location_id);
  if (filters.user_id) qs.set('user_id', filters.user_id);
  if (filters.shift_id) qs.set('shift_id', filters.shift_id);
  if (filters.status) qs.set('status', filters.status);
  if (filters.page) qs.set('page', String(filters.page));
  if (filters.limit) qs.set('limit', String(filters.limit));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export const shiftReportsApi = {
  list(filters: ShiftReportFilters): Promise<ShiftReportResponse> {
    // The body is { data, meta, totals }: apiClient would keep only `data`
    // (the rows), and the page then read `.data` of an array — always empty.
    return withFallback(
      () => apiClientEnvelope<ShiftReportResponse>(`/attendance/reports${toQuery(filters)}`),
      emptyResponse,
    );
  },

  bills(attendanceId: string): Promise<SessionBillRow[]> {
    return withFallback(
      () => apiClient<SessionBillRow[]>(`/attendance/${attendanceId}/bills`),
      [],
    );
  },
};
